import type React from "react";
import { Fragment } from "react";
import { cn } from "@/lib/utils";

type Segment = { text: string; emphasis: boolean };

/** Splits `text` around the first occurrence of `emphasis`. */
const splitEmphasis = (text: string, emphasis?: string): Segment[] => {
	const index = emphasis ? text.indexOf(emphasis) : -1;
	if (!emphasis || index < 0) return [{ text, emphasis: false }];
	return [
		{ text: text.slice(0, index), emphasis: false },
		{ text: emphasis, emphasis: true },
		{ text: text.slice(index + emphasis.length), emphasis: false },
	].filter((segment) => segment.text.length > 0);
};

/**
 * Display title whose words rise out of their own mask on first paint.
 * Pure CSS, so the heading paints before hydration. `emphasis` is a substring
 * of `text`, tinted like an editor selection once the words are up.
 */
export function MaskText({
	text,
	emphasis,
	delay = 0,
	animate = true,
	caret = false,
}: {
	text: string;
	emphasis?: string;
	/** Delay in seconds before the first word. */
	delay?: number;
	/** `false` renders the same markup without the entrance. */
	animate?: boolean;
	/** Ends the title with a blinking terminal cursor. */
	caret?: boolean;
}) {
	// Words are groups of non-space pieces, so punctuation glued to the
	// emphasis ("travailler.") stays in the same mask.
	const words: Segment[][] = [];
	let current: Segment[] = [];
	for (const segment of splitEmphasis(text, emphasis)) {
		for (const piece of segment.text.split(/([ \t\n]+)/)) {
			if (piece.length === 0) continue;
			// Non-breaking spaces stay inside a word: « résoudre ? » never wraps.
			if (/^[ \t\n]+$/.test(piece)) {
				if (current.length) words.push(current);
				current = [];
			} else {
				current.push({ text: piece, emphasis: segment.emphasis });
			}
		}
	}
	if (current.length) words.push(current);

	return (
		<span style={{ "--delay": `${delay}s` } as React.CSSProperties}>
			{words.map((pieces, i) => {
				const key = `${i}-${pieces.map((piece) => piece.text).join("")}`;
				const inner = (
					<span>
						{pieces.map((piece, pieceIndex) => (
							<span
								key={`${piece.text}-${pieceIndex}`}
								className={cn(
									piece.emphasis && "marker",
									// Bridge the space to the next highlighted word.
									piece.emphasis &&
										pieceIndex === pieces.length - 1 &&
										words[i + 1]?.[0]?.emphasis &&
										"marker-join",
								)}
							>
								{piece.text}
							</span>
						))}
						{caret && i === words.length - 1 ? (
							<span aria-hidden="true" className="caret-block" />
						) : null}
					</span>
				);
				return (
					<Fragment key={key}>
						{i > 0 ? " " : null}
						{animate ? (
							<span
								className="mask-word"
								style={{ "--i": i } as React.CSSProperties}
							>
								{inner}
							</span>
						) : (
							inner
						)}
					</Fragment>
				);
			})}
		</span>
	);
}
